// src/terminal/commands/filesystem.js

export function registerFilesystemCommands(commandMap, fsState, addLines) {

  function formatLsOutput(result) {
    const lines = [{ type: 'blank' }];

    result.entries.forEach(entry => {
      const isDir = entry.type === 'dir';
      const locked = !entry.accessible;
      let color = '#cccccc';
      let suffix = '';
      let prefix = '  ';

      if (isDir) {
        color = locked ? '#ff0040' : '#00e5ff';
        suffix = '/';
      } else {
        color = locked ? '#ff0040' : '#00ff41';
      }

      if (locked) {
        prefix = '  🔒 ';
      }

      lines.push({
        type: 'line',
        text: `${prefix}${entry.name}${suffix}${locked ? '  [ACCESS DENIED]' : ''}`,
        color,
      });
    });

    lines.push({ type: 'blank' });
    return lines;
  }

  // LS command
  commandMap['ls'] = (args) => {
    const target = args[0] || null;
    const result = fsState.ls(target);

    if (!result.success) {
      addLines([
        { type: 'blank' },
        { type: 'line', text: `  ${result.error}`, color: '#ff0040' },
        { type: 'blank' },
      ]);
      return;
    }

    addLines(formatLsOutput(result));
  };

  // DIR command (alias for ls)
  commandMap['dir'] = commandMap['ls'];

  // CD command
  commandMap['cd'] = (args) => {
    const target = args[0] || '~';
    const result = fsState.cd(target);

    if (!result.success) {
      addLines([
        { type: 'blank' },
        { type: 'line', text: `  ${result.error}`, color: '#ff0040' },
        { type: 'blank' },
      ]);
      return;
    }

    // Silent success (like real cd), but show path
    addLines([
      { type: 'line', text: `  ${result.path}`, color: '#555' },
    ]);
  };

  // CAT command
  commandMap['cat'] = (args) => {
    const target = args[0];
    const result = fsState.cat(target);

    if (!result.success) {
      addLines([
        { type: 'blank' },
        { type: 'line', text: `  ${result.error}`, color: '#ff0040' },
        { type: 'blank' },
      ]);
      return;
    }

    // Split content into lines and display
    const contentLines = result.content.split('\n').map(line => ({
      type: 'line',
      text: line,
      color: '#cccccc',
    }));

    addLines([
      { type: 'blank' },
      ...contentLines,
      { type: 'blank' },
    ]);
  };

  // PWD command
  commandMap['pwd'] = () => {
    addLines([
      { type: 'blank' },
      { type: 'line', text: `  ${fsState.currentPath}`, color: '#00ff41' },
      { type: 'blank' },
    ]);
  };

  // TREE command (simplified)
  commandMap['tree'] = (args) => {
    const target = args[0] || fsState.currentPath;
    const node = fsState.resolve(target);

    if (!node || node.type !== 'dir') {
      addLines([
        { type: 'blank' },
        { type: 'line', text: `  tree: ${target}: Not a directory`, color: '#ff0040' },
        { type: 'blank' },
      ]);
      return;
    }

    const lines = [{ type: 'blank' }];
    lines.push({ type: 'line', text: `  ${target}`, color: '#00e5ff' });

    function walk(dirNode, indent) {
      const entries = Object.entries(dirNode.children);
      entries.forEach(([name, child], idx) => {
        const isLast = idx === entries.length - 1;
        const connector = isLast ? '└── ' : '├── ';
        const accessible = fsState.canAccess(child);
        const isDir = child.type === 'dir';

        let color = '#cccccc';
        if (!accessible) color = '#ff0040';
        else if (isDir) color = '#00e5ff';
        else color = '#00ff41';

        const lockIcon = !accessible ? ' 🔒' : '';
        lines.push({
          type: 'line',
          text: `  ${indent}${connector}${name}${isDir ? '/' : ''}${lockIcon}`,
          color,
        });

        if (isDir && accessible) {
          const nextIndent = indent + (isLast ? '    ' : '│   ');
          walk(child, nextIndent);
        }
      });
    }

    if (fsState.canAccess(node)) {
      walk(node, '');
    } else {
      lines.push({ type: 'line', text: '  [ACCESS DENIED]', color: '#ff0040' });
    }

    lines.push({ type: 'blank' });
    addLines(lines);
  };

  // FIND command
  commandMap['find'] = (args) => {
    const searchTerm = args.join(' ').toLowerCase();
    if (!searchTerm) {
      addLines([
        { type: 'blank' },
        { type: 'line', text: '  Usage: find <filename>', color: '#ffb000' },
        { type: 'blank' },
      ]);
      return;
    }

    const results = [];
    function search(node, path) {
      if (node.type === 'dir' && node.children) {
        Object.entries(node.children).forEach(([name, child]) => {
          const fullPath = path === '/' ? `/${name}` : `${path}/${name}`;
          if (name.toLowerCase().includes(searchTerm)) {
            results.push({
              path: fullPath,
              type: child.type,
              accessible: fsState.canAccess(child),
            });
          }
          if (child.type === 'dir') {
            search(child, fullPath);
          }
        });
      }
    }

    search(fsState.resolve('/'), '/');

    if (results.length === 0) {
      addLines([
        { type: 'blank' },
        { type: 'line', text: `  No files matching "${searchTerm}" found.`, color: '#555' },
        { type: 'blank' },
      ]);
    } else {
      const lines = [{ type: 'blank' }];
      results.forEach(r => {
        lines.push({
          type: 'line',
          text: `  ${r.accessible ? '' : '🔒 '}${r.path}`,
          color: r.accessible ? (r.type === 'dir' ? '#00e5ff' : '#00ff41') : '#ff0040',
        });
      });
      lines.push({ type: 'blank' });
      addLines(lines);
    }
  };
}